import { BillingIntervalUnit } from '../../__generated__/globalTypes';

/** How often a price debits, as the server reports the Stripe price's own recurrence. */
export interface IBillingCadence {
  interval: BillingIntervalUnit;
  intervalCount: number;
}

// Keyed on the enum so a unit added later demands explicit copy rather than
// silently borrowing a neighbor's.
const SINGLE_UNIT_LABEL: Record<BillingIntervalUnit, string> = {
  [BillingIntervalUnit.Day]: 'Daily',
  [BillingIntervalUnit.Week]: 'Weekly',
  [BillingIntervalUnit.Month]: 'Monthly',
  [BillingIntervalUnit.Year]: 'Annual',
};

const SINGLE_UNIT_FREQUENCY: Record<BillingIntervalUnit, string> = {
  [BillingIntervalUnit.Day]: 'daily',
  [BillingIntervalUnit.Week]: 'weekly',
  [BillingIntervalUnit.Month]: 'monthly',
  [BillingIntervalUnit.Year]: 'annually',
};

const UNIT_PLURAL: Record<BillingIntervalUnit, string> = {
  [BillingIntervalUnit.Day]: 'days',
  [BillingIntervalUnit.Week]: 'weeks',
  [BillingIntervalUnit.Month]: 'months',
  [BillingIntervalUnit.Year]: 'years',
};

/** Names the cadence as a plan option: "Annual", "Weekly", "Every 2 weeks". */
export function cadenceLabel(cadence: IBillingCadence): string {
  return cadence.intervalCount === 1
    ? SINGLE_UNIT_LABEL[cadence.interval]
    : `Every ${cadence.intervalCount} ${UNIT_PLURAL[cadence.interval]}`;
}

/**
 * How often the membership bills, phrased to follow a verb: "monthly",
 * "every 2 weeks". A multi-unit cadence has no adverb, so the phrase reads
 * correctly only after "Billed" or "billing", never before it.
 */
export function cadenceFrequency(cadence: IBillingCadence): string {
  return cadence.intervalCount === 1
    ? SINGLE_UNIT_FREQUENCY[cadence.interval]
    : `every ${cadence.intervalCount} ${UNIT_PLURAL[cadence.interval]}`;
}

/** Whether two cadences debit on the same rhythm. */
export function sameCadence(a: IBillingCadence, b: IBillingCadence): boolean {
  return a.interval === b.interval && a.intervalCount === b.intervalCount;
}
